/*
 * Copyright (c) 2021-2021. Datafood srl
 * Developed by Revo Digital - revodigital.it
 * ---
 * Author: leonardoviada
 * File: utils.tsx
 * Project: mle-client
 * Committed last: 2021/11/12 @ 840
 * ---
 */

import {history} from './history';
import {MeasureUnits, OSs, ShapeTypes, Themes} from './enums';
import {SelectProps} from '@blueprintjs/select';
import {IconName, Intent} from '@blueprintjs/core';
import {pixelToMeasureUnit} from './measure-unit-utils';
import {Editor} from '../store/observables/Editor';
import {Size2D} from '@revodigital/pamela/lib/common/Size2D';
import {Builtins} from '@revodigital/mle-dataresolver';
import {dataRefNameOf} from '@revodigital/mpes/lib/components/data/DataRefNameFactory';
import {BuiltinDataRef, IDataRef} from '@revodigital/mpes';

/**
 * Evita l'utilizzo di stringhe hard-coded per la gestione della chiave tema in localStorage.
 * @type {string}
 */
export const themeStorageKey = 'MLE_THEME';

/**
 * Determina se il tema corrente è chiaro o scuro leggendo la chiave dal localStorage.
 * Utilizzato per renderizzare dinamicamente alcune componenti dell'work-area.
 * @returns {boolean}
 */
export const currentThemeIsDark = (): boolean => {
    return localStorage.getItem(themeStorageKey) === Themes.DARK || !localStorage.getItem(
        themeStorageKey);
};

/**
 * Wrapper del redirect basato sulla cronologia di React Router, riporta in console
 * il percorso di destinazione ogni volta che viene effettuata l'operazione.
 * @param {string} destPath
 */
export const redirect = (destPath: string) => {
    console.log(`Redirecting to ${destPath}...`);
    history.push(destPath);
};

/**
 * Trasforma un'enumerazione in vettore utilizzabile dal componente Select di BlueprintJs
 * @param o
 * @returns {any[]}
 */
export const objectToSelectItemsArray = (o: any): NameValue[] => {
    let ret: NameValue[] = [];
    for (const key of Object.keys(o))
        ret.push({name: key, value: o[key]});

    return ret;
};

export const valueToSelectObject = (item: string, en: any): NameValue | undefined => {
    if (!item) return undefined;

    const all = objectToSelectItemsArray(en);

    const res = all.filter(it => it.value === item);
    if (res.length > 0)
        return res[0];
    return undefined;
}

/**
 * Trasforma un vettore di stringhe in vettore utilizzabile dal componente Select di BlueprintJs
 * @param {string[]} stringArr
 * @returns {{name: string, value: string}[]}
 */
export const stringArrayToSelectItemsArray = (stringArr: string[]) => {
    return stringArr.map(item => ({
        name: item,
        value: item
    }));
};

/**
 * Calcola la larghezza del canvas applicando le giuste proporzioni
 */
export const calculateCanvasWidth = () => {
    const outerCanvasContainer = document.getElementById(MLE_CANVAS_WRAPPER);

    if (outerCanvasContainer)
        return outerCanvasContainer.clientWidth;
};

/**
 * Calcola l'altezza del canvas in base alle proporzioni attuali
 */
export const calculateCanvasHeight = () => {
    const outerCanvasContainer = document.getElementById(MLE_CANVAS_WRAPPER);

    if (outerCanvasContainer)
        return outerCanvasContainer.clientHeight - 30;
};

/**
 * Rappresenta i parametri del componente Select adattato all'utilizzo all'interno di form
 */
export type SelectPropsFrom<T> = Omit<SelectProps<T>,
        | 'createNewItemFromQuery'
        | 'createNewItemRenderer'
        | 'items'
        | 'itemsEqual'
        | 'itemRenderer'
        | 'noResults'
        | 'onItemSelect'>
    & {
    allowCreate?: boolean;
};

/**
 * Converte una misura di dimensione (width, height) in pixel in una stringa leggibile,
 * convertendola in una determinata unità di misura
 * @param size Dimensione
 * @param measureUnit Unità di misura
 */
export const stringifySizeWithMeasure = (size: Size2D, measureUnit: MeasureUnits): string =>
    `${pixelToMeasureUnit(measureUnit,
        size.getWidth()).toFixed(2)}x${pixelToMeasureUnit(measureUnit,
        size.getHeight()).toFixed(2)}${measureUnit}`;

/**
 * Identifica un'entità provvista di nome, con tipo string
 * @interface
 */
export interface HasName {
    name: string;
}

export interface NameValue<T = any> extends HasName {
    value: T;
}

/**
 * Rappresenta i parametri del componente Select di BlueprintJs
 * adattato per l'utlizzo in MLEditor.
 */
export type MleSelectProps<T extends HasName> = Partial<SelectPropsFrom<T>> & {
    name: string;
    icon: IconName;
    items: T[];
    initalValue?: T;
    renderLabel: (item: T) => string;
    renderText: (item: T) => string;
    onSelect: (item: T) => void;
    intent?: Intent;
}

/**
 * Identifica univocamente il nodo DOM su cui verrà montato il piano di lavoro principale
 * @type {string}
 */
export const MLE_CANVAS_ID = 'mle-canvas';

/**
 * Wrapper id utilizzato come wrapper per il canvas
 * Utile per realizzare il ridimensionamento responsive del canvas
 */
export const MLE_CANVAS_WRAPPER = 'mle-canvas-wrapper';

/**
 * Rappresenta l'associazione tra un tipo forma e un'icona.
 */
interface ShapeIconAssociation {
    [key: string]: IconName;
}

/**
 * Rappresenta l'associazione tra un tipo forma e la sua denominazione completa, più chiara all'utente.
 */
interface ShapeHumanReadableNameAssociation {
    [key: string]: string;
}

/**
 * Associa ciascun tipo forma alla corrispondente icona.
 * Evita la presenza di associazioni inconsistenti nell'work-area.
 * @type {ShapeIconAssociation}
 */
export const shapeIconAssociation = {
    [ShapeTypes.RECT]: 'square',
    [ShapeTypes.CIRCLE]: 'circle',
    [ShapeTypes.TRIANGLE]: 'symbol-triangle-up',
    [ShapeTypes.ELLIPSE]: 'stadium-geometry',
    [ShapeTypes.RICH_TEXT]: 'paragraph',
    [ShapeTypes.TEXT]: 'text-highlight',
    [ShapeTypes.LINE]: 'flow-linear',
    [ShapeTypes.POLYLINE]: 'layout-linear',
    [ShapeTypes.POLYGON]: 'polygon-filter',
    [ShapeTypes.TABLE]: 'th',
    [ShapeTypes.BARCODE]: 'barcode',
    [ShapeTypes.WEDGE]: 'dot',
    [ShapeTypes.EXPORT_VARIABLE]: 'variable',
    [ShapeTypes.IMAGE]: 'media'
} as ShapeIconAssociation;

/**
 * Associa ciascun tipo forma alla corrispondente denominazione completa.
 * Evita la presenza di associazioni inconsistenti nell'work-area.
 * @type {ShapeHumanReadableNameAssociation}
 */
export const shapeHumanReadableNameAssociation = {
    [ShapeTypes.RECT]: 'Rettangolo',
    [ShapeTypes.CIRCLE]: 'Cerchio',
    [ShapeTypes.TRIANGLE]: 'Triangolo',
    [ShapeTypes.ELLIPSE]: 'Ellisse',
    [ShapeTypes.RICH_TEXT]: 'Testo RTF',
    [ShapeTypes.TEXT]: 'Testo semplice',
    [ShapeTypes.LINE]: 'Linea',
    [ShapeTypes.POLYLINE]: 'Polilinea',
    [ShapeTypes.POLYGON]: 'Poligono',
    [ShapeTypes.TABLE]: 'Tabella',
    [ShapeTypes.BARCODE]: 'Codice a barre',
    [ShapeTypes.WEDGE]: 'Cuneo',
    [ShapeTypes.EXPORT_VARIABLE]: 'Variabile esportazione',
    [ShapeTypes.IMAGE]: 'Immagine'
} as ShapeHumanReadableNameAssociation;

/**
 * Identifica il sistema operativo in uso sulla macchina corrente e ne restituisce il nome tramite apposita enumerazione.
 * @returns {OSs} Se il sistema viene rilevato correttamente ed è tra quelli supportati
 * @returns {null} Se il sistema non viene rilevato correttamente o non è supportato
 */
export const getOS = (showIcons: boolean = false): OSs | undefined => {
    const platform = window.navigator.platform,
        macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
        windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
    let os;

    if (macosPlatforms.indexOf(platform) !== -1)
        os = OSs.MAC;
    else if (windowsPlatforms.indexOf(platform) !== -1)
        os = OSs.WINDOWS;
    else if (/Linux/.test(platform))
        os = OSs.LINUX;

    if (!showIcons)
        return os;

    return `${os} ${OSIcons[os as OSs]}` as OSs;
};

export const OSIcons = {
    [OSs.MAC]: '',
    [OSs.LINUX]: '🐧',
    [OSs.WINDOWS]: '🤡'
};

export const isEditor = (o: any): o is Editor => !!o;

export const getShapeTypeFromId = (shapeId: string): ShapeTypes => shapeId.split(
    '_')[1] as ShapeTypes;

export const withIcons = (combo: string, os = getOS()) => combo
    .replace('shift', '⇧')
    .replace('cmd', '⌘')
    .replace('alt', os === OSs.MAC ? '⎇' : 'alt')
    .replace('option', os === OSs.MAC ? '⎇' : 'option')
    .replace('del', '⌫')
    .replace('backspace', '⌫')
    .replaceAll('+', os === OSs.MAC ? '' : '+')
    .replaceAll(' ', '')
    .toLocaleUpperCase();


/**
 * Calcola il nome di un'asset partendo dal suo bucketURL
 * @param bucketURL
 */
export const getAssetNameByBucketUrl = (bucketURL: string): string => {
    if (!bucketURL) return '';
    const part1 = bucketURL.substring(bucketURL.lastIndexOf('/') + 1).slice();

    return part1.substring(part1.indexOf('-', 0) + 1);
};

export const withEllipsis = (str: string, maxLength: number = 20) => {
    return `${str.substring(0,
        maxLength)}${str.length > maxLength ? '...' : ''}`;
};

/**
 * Genera un dataurl valido per un'immagine codificata in base64
 * @param base64
 */
export const dataURLFromBase64 = (base64: string): string => {
    return 'data:image/png;base64,' + base64;
};

export const getOrientation = (width: number, height: number): 'l' | 'p' => {
    if (height >= width) return 'p';
    return 'l';
};

/**
 * Restituisce il decimale (multiplo di 10) più vicino ad un numero dato
 * @param num
 */
export const nearestDecimal = (n: number): number => {
    if (n > 0)
        return Math.ceil(n / 10.0) * 10;
    else if (n < 0)
        return Math.floor(n / 10.0) * 10;
    else
        return 10;
};

/**
 * Restituisce il decimale più vicino o 0
 * @param n
 */
export const nearestDecimalOrZero = (n: number): number => {
    const val = Math.round(n / 10.0) * 10;

    if (n - val > n / 2) return Math.floor(val);
    else return Math.round(val);
};


/**
 * Restituisce il multiplo più vicino
 * @param n
 * @param multiple
 */
export const nearestMultipleOf = (n: number, multiple: number): number => {
    if (n > 0)
        return Math.ceil(n / (multiple * 1.0)) * multiple;
    else if (n < 0)
        return Math.floor(n / (multiple * 1.0)) * multiple;
    else
        return multiple;
};

export const initContentMatrix = (columnsCount: number, rowsCount: number): string[][] => {
    return new Array(columnsCount).fill(new Array(rowsCount));
};

export const stringContainsOne = (str: string, find: string[]): boolean => {
    for (const f of find)
        if (str.includes(f)) return true;

    return false;
};

export const getAllBuiltinNames = (): string[] => {
    return Object.values(Builtins);
};

export const containsBuiltinRef = (src: string): boolean => {
    const builtins = Object.values(Builtins).map(it => dataRefNameOf(it));

    return (stringContainsOne(src, builtins));
};

export const containsAllBuiltins = (ref: IDataRef[]): boolean => {
    const allBuiltins = getAllBuiltinNames();

    for (const bt of allBuiltins)
        if (ref.filter(it => it.name === bt).length === 0) return false;

    return true;
};

export const addMissingBuiltins = (ref: IDataRef[]) => {
    const allBuiltins = Object.keys(Builtins);

    for (const bt of allBuiltins)
        if (ref.filter(it => it.name === bt).length === 0) ref.push(new BuiltinDataRef(
            bt));
};

export const normalizeDataRefs = (refs: IDataRef[]): IDataRef[] => {
    let found: IDataRef[] = [];

    refs.forEach(it => {
        if (found.filter(d => d.name === it.name).length === 0) found.push(it);
    });

    return found;
};